import { useState } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import { Send, Mail, Phone, Clock, CheckCircle, Building } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", subject: "", message: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await axios.post(`${API_URL}/api/contact`, form);
      setSent(true);
      toast.success("Message envoye !");
    } catch (error) {
      toast.error("Erreur lors de l'envoi. Veuillez reessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07090E]">
      <PublicNavbar />

      <section className="pt-32 pb-20 sm:pt-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-4">Contact</p>
            <h1
              className="text-4xl sm:text-5xl font-bold text-white tracking-tight mb-4"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Parlons de vos besoins
            </h1>
            <p className="text-lg text-slate-400 max-w-xl mx-auto">
              Une question, une demande de demo ou un projet specifique ? Notre equipe est la pour vous aider.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact form */}
            <div className="lg:col-span-3">
              {sent ? (
                <div className="p-10 rounded-2xl border border-emerald-500/20 bg-[#0D111A]/70 text-center" data-testid="contact-success">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="text-emerald-400" size={32} />
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-2">Message envoye</h2>
                  <p className="text-slate-400">Merci pour votre message. Notre equipe vous repondra sous 24 heures ouvrables.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="p-8 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70 space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">Nom *</Label>
                      <Input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom"
                        className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">Email *</Label>
                      <Input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="vous@entreprise.com"
                        className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">Entreprise</Label>
                      <Input
                        name="company"
                        value={form.company}
                        onChange={handleChange}
                        placeholder="Nom de votre entreprise"
                        className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300 text-sm">Sujet *</Label>
                      <Input
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        required
                        placeholder="Sujet de votre message"
                        className="bg-[#07090E] border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500"
                        data-testid="contact-subject-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-300 text-sm">Message *</Label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Decrivez votre besoin..."
                      className="w-full rounded-md bg-[#07090E] border border-slate-700 text-white placeholder:text-slate-600 focus:border-cyan-500 outline-none p-3 text-sm resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-5 rounded-xl w-full sm:w-auto"
                    data-testid="contact-submit-btn"
                  >
                    {isLoading ? "Envoi..." : "Envoyer le message"}
                    <Send size={16} className="ml-2" />
                  </Button>
                </form>
              )}
            </div>

            {/* Contact info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="p-6 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70">
                <Mail className="text-cyan-400 mb-3" size={22} />
                <h3 className="text-white font-semibold mb-1">Email</h3>
                <p className="text-slate-400 text-sm">contact@serveuronline.com</p>
              </div>

              <div className="p-6 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70">
                <Phone className="text-emerald-400 mb-3" size={22} />
                <h3 className="text-white font-semibold mb-1">Telephone</h3>
                <p className="text-slate-400 text-sm">+33 1 23 45 67 89</p>
              </div>

              <div className="p-6 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70">
                <Clock className="text-amber-400 mb-3" size={22} />
                <h3 className="text-white font-semibold mb-1">Horaires</h3>
                <p className="text-slate-400 text-sm">Lundi - Vendredi</p>
                <p className="text-slate-400 text-sm">9h00 - 18h00 (CET)</p>
              </div>

              <div className="p-6 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70">
                <Building className="text-indigo-400 mb-3" size={22} />
                <h3 className="text-white font-semibold mb-1">Siege</h3>
                <p className="text-slate-400 text-sm">France</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
